import React, { useEffect } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import Typography from '@material-ui/core/Typography';
import FormTextField from './FormTextField';
import { Checkbox, useMediaQuery } from '@material-ui/core';
import dayjs from 'dayjs';
import FormDatePicker from './FormDatePicker';

export type Phase = {
  enabled: boolean;
  label?: string;
  start?: string | null;
  completion?: string | null;
  weeks?: number | null;
  completionManual: boolean;
};

export const PHASES: Array<{ key: string; label: string; allowCustomLabel?: boolean }> = [
  { key: 'programming', label: 'Programming' },
  { key: 'schematicDesign', label: 'Schematic Design (SD)' },
  { key: 'designDevelopment', label: 'Design Development (DD)' },
  { key: 'constructionDocuments', label: 'Construction Documents (CD)' },
  { key: 'permitting', label: 'Permitting' },
  { key: 'biddingProcurement', label: 'Bidding / Procurement' },
  { key: 'constructionAdministration', label: 'Construction Administration (CA)' },
  { key: 'closeout', label: 'Closeout' },
];

function addWeeks(startISO: string, weeks: number): string | null {
  const base = dayjs(startISO);
  if (!base.isValid() || !Number.isFinite(weeks)) return null;
  return base.add(weeks, 'week').format('YYYY-MM-DD');
}

export default function ProjectSchedulePhaseRow({
  control,
  setValue,
  phaseKey,
  label,
  allowCustomLabel,
}: {
  control: Control<any>;
  setValue: (name: string, value: any, opts?: any) => void;
  phaseKey: string;
  label: string;
  allowCustomLabel?: boolean;
}) {
  const isMobile = useMediaQuery('(max-width: 800px)');

  const enabled = useWatch({ control, name: `schedule.${phaseKey}.enabled` }) as boolean;
  const start = useWatch({ control, name: `schedule.${phaseKey}.start` }) as string | null;
  const weeks = useWatch({ control, name: `schedule.${phaseKey}.weeks` }) as number | null;
  const completionManual = useWatch({
    control,
    name: `schedule.${phaseKey}.completionManual`,
  }) as boolean | undefined;

  useEffect(() => {
    if (!enabled) return;
    if (!start || !weeks || Number.isNaN(weeks)) return;
    if (completionManual) return;

    const auto = addWeeks(start, Number(weeks));
    setValue(`schedule.${phaseKey}.completion`, auto, { shouldDirty: true, shouldValidate: true });
  }, [enabled, start, weeks, completionManual, phaseKey, setValue]);

  const disabled = !enabled || control._formState.disabled;

  const checkbox = (
    <Controller
      control={control}
      name={`schedule.${phaseKey}.enabled`}
      render={({ field }) => (
        <Checkbox
          {...field}
          checked={!!field.value}
          onChange={(e) => field.onChange(e.target.checked)}
        />
      )}
    />
  );

  const labelField = allowCustomLabel ? (
    <FormTextField
      name={`schedule.${phaseKey}.label`}
      placeholder={allowCustomLabel ? 'Other' : undefined}
      control={control}
      label={label}
      variant="outlined"
      size="small"
      fullWidth
      disabled={disabled}
      style={{ maxWidth: 250 }}
    />
  ) : (
    <Typography>{label}:</Typography>
  );

  const startDateField = (
    <FormDatePicker
      name={`schedule.${phaseKey}.start`}
      label="Start"
      control={control}
      disabled={disabled}
    />
  );

  const completionDateField = (
    <FormDatePicker
      name={`schedule.${phaseKey}.completion`}
      label="Completion"
      control={control}
      disabled={disabled}
    />
  );

  const weeksField = (
    <FormTextField
      name={`schedule.${phaseKey}.weeks`}
      label={isMobile ? 'Weeks' : undefined}
      control={control}
      disabled={disabled}
      inputProps={{ min: 0, step: 1 }}
      InputLabelProps={isMobile ? { shrink: true } : undefined}
    />
  );

  if (isMobile) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {checkbox}
          {labelField}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {startDateField}
          {completionDateField}
          {weeksField}
        </div>
      </>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 0.2fr 1fr 1fr 0.4fr',
        alignItems: 'center',
        gap: '12px',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {checkbox}
        {labelField}
      </div>

      <div />

      {startDateField}
      {completionDateField}
      {weeksField}
    </div>
  );
}
